import { useEffect, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ContextMenu } from "../../contextMenu/ContextMenu";
import { Directory } from "../../../models/Directory";
import {
  CheckIcon,
  DotsThreeOutlineIcon,
  DotsThreeVerticalIcon,
  FileArchiveIcon,
  FileIcon,
  FileImageIcon,
  FileSvgIcon,
  FileTextIcon,
  FolderIcon,
  FolderOpenIcon,
  MicrosoftPowerpointLogoIcon,
  MicrosoftWordLogoIcon,
} from "@phosphor-icons/react";
import { FilePdfIcon } from "@phosphor-icons/react/dist/ssr";
import { useProject } from "../../../stores/useProject";
import { useProfile } from "../../../stores/useProfile";
import { FileEditor } from "../../fileEditor/fileEditor";
import { File } from "../../../models/File";
import { TreeRow } from "../../../models/global/Types";
import { base64ToBlob } from "../../../globals/utils";

type DirectoryRowProps = {
  row: TreeRow;
  depth: number;
  treeVersion: number;
  openedContextMenu: {
    row: TreeRow;
    id: number;
  } | null;
  onChange: () => void;
  onClick: () => void;
  onContextMenu: (row: TreeRow) => void;
};

export const DirectoryRow = ({
  row,
  depth,
  treeVersion,
  openedContextMenu,
  onChange,
  onClick,
  onContextMenu,
}: DirectoryRowProps) => {
  const { addDirectory, updateDirectory, removeDirectory, addFile, moveFile } =
    useProject();
  const { user } = useProfile();

  const isDirectory = row.type === "directory";
  const directory = isDirectory ? row.data : null;
  const file = !isDirectory ? row.data : null;

  const [contextMenuOpened, setContextMenuOpened] = useState<boolean>(false);

  const [editMode, setEditMode] = useState(false);
  const [newName, setNewName] = useState(
    isDirectory ? directory!.name : file!.filename,
  );
  const [collapsed, setCollapsed] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [darkOverlayOpened, setDarkOverlayOpened] = useState(false);

  const closeMenu = () => setContextMenuOpened(false);

  const ChangeName = async () => {
    if (!user) return;
    if (isDirectory) {
      await updateDirectory(user, directory!.id, newName);
    }
    setEditMode(false);
    onChange();
  };

  const handleOpen = () => {
    setDarkOverlayOpened(true);
    closeMenu();
    onClick();
  };

  const handleClose = () => {
    setDarkOverlayOpened(false);
    onChange();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (!isDirectory) return;
    if (e.dataTransfer.types.includes("Files")) {
      e.preventDefault();
      setDragOver(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isDirectory) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = async (e: React.DragEvent) => {
    if (!isDirectory || !user) return;

    e.preventDefault();
    setDragOver(false);

    const internalData = e.dataTransfer.getData("application/x-project-file");

    if (internalData) {
      const { fileId, fromDirectoryId } = JSON.parse(internalData);
      if (fromDirectoryId !== directory!.id) {
        await moveFile(user, fileId, directory!.id);
        onChange();
      }
      return;
    }

    for (const droppedFile of Array.from(e.dataTransfer.files)) {
      await addFile(user, directory!.id, droppedFile);
    }

    onChange();
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split(".").pop();
    switch (ext) {
      case "pdf":
        return <FilePdfIcon size={20} />;
      case "txt":
        return <FileTextIcon size={20} />;
      case "zip":
        return <FileArchiveIcon size={20} />;
      case "jpeg":
      case "png":
      case "jpg":
        return <FileImageIcon size={20} />;
      case "svg":
        return <FileSvgIcon size={20} />;
      case "docx":
        return <MicrosoftWordLogoIcon size={20} />;
      case "pptx":
      case "xlsx":
        return <MicrosoftPowerpointLogoIcon size={20} />;
      default:
        return <FileIcon size={20} />;
    }
  };

  const handleFileDownload = () => {
    console.log("here");
    if (!file) return;
    const blob = base64ToBlob(file.base64, file.mimeType);
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = file.filename;
    a.click();

    URL.revokeObjectURL(url);
  };

  const isMenuOpen =
    openedContextMenu?.row.type === row.type &&
    openedContextMenu?.id === row.data.id;

  return (
    <Box className={`directoryRow ${dragOver ? "fileDrag" : ""}`}>
      <Box
        className={isDirectory ? "folder" : "file"}
        sx={{
          paddingLeft: `${depth * 24}px`,
          userSelect: "none",
          cursor: isDirectory ? "pointer" : "grab",
        }}
        draggable={!isDirectory}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <Box
          className="data"
          onClick={() => {
            isDirectory && setCollapsed(!collapsed);
            onClick();
          }}>
          {isDirectory ? (
            collapsed ? (
              <FolderIcon size={20} />
            ) : (
              <FolderOpenIcon
                size={20}
                onClick={() => setCollapsed(!collapsed)}
              />
            )
          ) : (
            getFileIcon(file!.filename)
          )}

          {editMode ? (
            <>
              <TextField
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <IconButton onClick={ChangeName}>
                <CheckIcon size={20} />
              </IconButton>
            </>
          ) : (
            <Typography variant="h5">
              {isDirectory ? directory!.name : file!.filename}
            </Typography>
          )}
        </Box>
        <Box className="contextMenuBtn" onClick={() => onContextMenu(row)}>
          <DotsThreeVerticalIcon className="dots" weight="bold" size={24} />
        </Box>
      </Box>

      {isDirectory && (
        <Collapse in={!collapsed}>
          {directory!.directories.map((dir) => (
            <DirectoryRow
              key={dir.id + treeVersion}
              openedContextMenu={openedContextMenu}
              row={{ type: "directory", data: dir }}
              depth={depth + 1}
              onChange={onChange}
              onClick={onClick}
              treeVersion={treeVersion}
              onContextMenu={onContextMenu}
            />
          ))}

          {directory!.files.map((f) => (
            <DirectoryRow
              key={f.id}
              row={{ type: "file", data: f }}
              openedContextMenu={openedContextMenu}
              depth={depth + 1}
              onChange={onChange}
              onClick={onClick}
              onContextMenu={onContextMenu}
              treeVersion={treeVersion}
            />
          ))}
        </Collapse>
      )}

      {isMenuOpen && (
        <ContextMenu
          row={openedContextMenu.row}
          onOpen={handleOpen}
          onChange={() => {
            onChange();
            closeMenu();
          }}
          onRename={() => {
            setEditMode(true);
            closeMenu();
          }}
          onDownload={handleFileDownload}
          onDownloadZip={async () => {}}
        />
      )}

      <FileEditor
        opened={darkOverlayOpened}
        file={file}
        onClose={handleClose}
      />
    </Box>
  );
};
